import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BookingReceipt from '../components/BookingReceipt';
import {
  Loader2,
  Home,
  CalendarCheck,
  Printer
} from 'lucide-react';

// Normalize backend URL
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

const SuccessPaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBookingDetails = async () => {
      const bookingId = searchParams.get('booking_id');
      
      if (!bookingId) {
        setError('No booking information found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API}/bookings/${bookingId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const booking = await response.json();
          
          // Transform booking data to payment format
          const paymentData = {
            id: booking.booking_id,
            transaction_code: booking.payment_transaction_id || booking.booking_id.slice(-8).toUpperCase(),
            name: user?.name || 'Customer',
            contact_number: user?.phone || '',
            email: user?.email || '',
            payment_type: booking.payment_method || 'online',
            payment_status: booking.payment_status || 'paid',
            total_amount: Number(booking.total_amount || 0).toFixed(2),
            created_at: booking.created_at || new Date().toISOString(),
            venue_name: booking.venue_name || 'CamSur Pickleball Club',
            booking_infos: [{
              id: 1,
              court_id: booking.court_id,
              court_no: booking.court_id?.replace(/\D/g, '') || '1',
              court_name: booking.court_name || `Court ${booking.court_id}`,
              player_count: booking.num_players || 2,
              booking_date: booking.date,
              booking_time_start: booking.start_time || (booking.time_slots?.[0] + ':00') || '00:00:00',
              booking_time_end: booking.end_time || '00:00:00',
              time_slots: booking.time_slots || [],
              duration_hours: booking.duration_hours || 1
            }],
            equipment_rentals: booking.equipment_rentals || [],
            add_ons: booking.add_ons || []
          };
          
          setPayment(paymentData);
        } else {
          setError('Failed to load booking details');
        }
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchBookingDetails();
    } else {
      setError('Please login to view booking details');
      setLoading(false);
    }
  }, [searchParams, token, user]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary" />
            <p className="text-muted-foreground">Loading your receipt...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !payment) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center space-y-4">
              <div className="h-16 w-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-red-600 text-2xl">!</span>
              </div>
              <h2 className="text-xl font-bold">{error || 'Booking not found'}</h2>
              <p className="text-muted-foreground">Unable to load your booking details.</p>
              <div className="pt-4 space-y-2">
                <Button onClick={() => navigate('/my-bookings')} className="w-full">
                  View My Bookings
                </Button>
                <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Custom actions for success page
  const customActions = (
    <>
      <Button 
        onClick={() => window.print()}
        className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-5 rounded-xl font-bold active:scale-95"
      >
        <Printer className="mr-2 h-4 w-4" />
        Print Receipt
      </Button>
      <Button
        variant="outline"
        onClick={() => navigate('/my-bookings')}
        className="flex-1 py-5 rounded-xl font-bold active:scale-95"
      >
        <CalendarCheck className="mr-2 h-4 w-4" />
        View My Bookings
      </Button>
    </>
  );

  return (
    <Layout>
      <div className="min-h-screen p-4 md:p-8 flex flex-col items-center print:p-0 print:m-0">
        <div className="w-full max-w-2xl">
          <BookingReceipt 
            payment={payment} 
            showHeader={true}
            showActions={true}
            variant="success"
            customActions={customActions}
          />
        </div>

        {/* Home button */}
        <div className="w-full max-w-2xl mt-4 no-print">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="w-full text-slate-500 hover:text-slate-700"
          >
            <Home className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </div>

        {/* Footer Note */}
        <p className="mt-6 text-center text-sm text-muted-foreground max-w-md no-print">
          A confirmation email has been sent to your registered email address. 
          Please save your transaction code for reference.
        </p>
      </div>
    </Layout>
  );
};

export default SuccessPaymentPage;
